import { Navigation } from "@/components/layout/Navigation";
import Hero from "@/components/marketing/Hero";
import Footer from "@/components/layout/Footer";
import { FeatureShowcase } from "@/components/marketing/FeatureShowcase";
import {
  MarketingArchetypeCard,
  MarketingTextAnalysisDemo,
  MarketingRewriteSuggestionsDemo,
  MarketingFactCheckDemo,
  MarketingTinderAngleDemo,
  MarketingMasonryArticleDemo,
  MarketingStylometricDemo,
  MarketingLexicalToolbarDemo
} from "@/components/marketing-demos";
import {
  Brain,
  Target,
  TrendingUp,
  BookOpen,
  Sparkles,
  CheckCircle,
  Lightbulb,
  Search,
  Zap,
  Fingerprint,
  SearchCheck
} from 'lucide-react';

const Features = () => {
  return (
    <div className="min-h-screen">
      <Navigation showAuth={true} className="mt-4" />

      {/* Hero Section */}
      <Hero
        badge="Copywriting Tools For Every Need"
        headline={
          <>
            All the tools you need to write{' '}
            <span className="font-serif font-medium italic text-primary">
              on-brand copy.
            </span>
          </>
        }
        description="Find your brand voice, or personal style. Arcitext helps you write like you."
      />

      {/* Feature Showcases - Alternating Layout */}
      <div className="divide-y divide-border">
        {/* Brand Voice Discovery */}
        <FeatureShowcase
          icon={Fingerprint}
          badge="Brand Voice Analysis"
          title="Discover Your Writing DNA"
          description="Upload URLs or text samples. Arcitext analyzes your writing and builds a writing style. Use it to stay consistent. Every time."
          features={[
            "Discover your writing style archetype",
            "Tone analysis across 6 dimensions",
            "Extract brand values, target audience, and word lists",
            "Works with any URL or text"
          ]}
          imagePosition="right"
          image={<div className="w-full h-full gap-4 grid md:grid-cols-2"><MarketingArchetypeCard archetype="explorer" /><MarketingArchetypeCard archetype="hero" /><MarketingArchetypeCard archetype="innocent" /><MarketingArchetypeCard archetype="magician" className="hidden md:grid" /><MarketingArchetypeCard archetype="creator" className="hidden md:grid" /><MarketingArchetypeCard archetype="everyman" className="hidden md:grid" /></div>}
        />

        {/* AI-Powered Writing */}
        <FeatureShowcase
          icon={Brain}
          badge="Your Personal Writing Coach"
          title="Feedback On Demand"
          description="Select any text to get feedback. Arcitext offers a workflow, with AI out of your way, but ready when you need it."
          features={[
            "Tone Fit scoring (does that sentence sound just right?)",
            "Rewrite suggestions that sound like you",
            "Relevant contextual feedback",
            "Fact-checking against credible sources"
          ]}
          imagePosition="left"
          image={<MarketingRewriteSuggestionsDemo />}
        />

        {/* Content Discovery */}
        <FeatureShowcase
          icon={Lightbulb}
          badge="Goodbye Writer's Block"
          title="Always Something to Write About"
          description="Browse curated RSS feeds or bring your own favorites. Get relevant content ideas from any article with AI."
          features={[
            "Curated feeds across 12+ categories",
            "Add your own RSS feeds",
            "Get content ideas from articles, that matches your purpose",
            "Hit the ground running with content outlines"
          ]}
          imagePosition="right"
          image={<MarketingMasonryArticleDemo />}
        />

        {/* Document Analysis */}
        <FeatureShowcase
          icon={CheckCircle}
          badge="Feedback Loops Go 'Poof'"
          title="Clarity Before Publishing"
          description="Get a full analysis of your content. See how well it matches your voice, purpose, and target audience."
          features={[
            "Overall quality score (0-100)",
            "Score breakdown across multiple dimensions",
            "Detailed feedback & suggestions",
            "Readability (LIX) score",
            "SEO keyword density"
          ]}
          imagePosition="left"
          image={<MarketingTextAnalysisDemo />}
        />

        {/* Fact Checking */}
        <FeatureShowcase
          icon={Lightbulb}
          badge="Accuracy"
          title="Fact-Check Your Claims"
          description="Select any claim and let Arcitext verify it. Get sources, accuracy scores, and suggestions to improve your copy."
          features={[
            "Web-search powered fact checking",
            "Credible source citations",
            "Truthfulness scoring",
            "Improvement suggestions"
          ]}
          imagePosition="right"
          image={<MarketingFactCheckDemo />}
        />
      </div>

      {/* Additional Features Grid */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-3xl font-bold text-foreground">Plus All The Essentials</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to maintain consistency and quality across all your content.
            </p>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto pt-8">
              <div className="glass-card p-6 flex flex-col items-center rounded-xl space-y-3">
                <BookOpen className="w-10 h-10 text-primary" />
                <h3 className="text-lg font-semibold">Content Repository</h3>
                <p className="text-sm text-muted-foreground">
                  Organize all your content with tags and status tracking.
                </p>
              </div>

              <div className="glass-card p-6 flex flex-col items-center rounded-xl space-y-3">
                <Target className="w-10 h-10 text-primary" />
                <h3 className="text-lg font-semibold">Purpose & Channel</h3>
                <p className="text-sm text-muted-foreground">
                  Define goals and optimize for specific platforms and audiences.
                </p>
              </div>

              <div className="glass-card p-6 flex flex-col items-center rounded-xl space-y-3">
                <SearchCheck className="w-10 h-10 text-primary" />
                <h3 className="text-lg font-semibold">SEO Optimization</h3>
                <p className="text-sm text-muted-foreground">
                  Track keyword density and get suggestions for better search visibility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
